import { I80Element, define, h } from "../base.js";
import { icon } from "../icons.js";
import { forward } from "../field.js";
import { I80RadioGroup } from "./form.js";

const POSITIONS = ["bottom", "left"];
const ALIGNMENTS = ["left", "center"];

/**
 * Only the chosen card may carry the checked class. The Ember component leaves this to the
 * consumer's own change handler; here the element keeps every card in the group in step.
 */
function syncChecked(input) {
  const scope = input.form ?? input.getRootNode();
  const peers = input.name ? scope.querySelectorAll(`input[type="radio"][name="${CSS.escape(input.name)}"]`) : [input];
  for (const peer of peers) {
    peer.closest(".i80-form-radio-card")?.classList.toggle("i80-form-radio-card--checked", peer.checked);
  }
}

/**
 * <i80-radio-card label="Admin" description="Full access" value="admin" icon="user" badge="Popular"></i80-radio-card>
 *
 * A radio button as a card: a <label> around a real <input type="radio">, so clicking
 * anywhere on the card selects it, the arrow keys move between cards and the form submits
 * the chosen value. Usually inside <i80-radio-card-group>, which supplies the name.
 * Anything else written inside the tag is kept inside the card's content, below the
 * generated label and description.
 */
export class I80RadioCard extends I80Element {
  static get observedAttributes() {
    return ["label", "description", "icon", "badge", "checked", "disabled", "max-width", "alignment", "control-position"];
  }

  setup() {
    let input = this.part("control") ?? this.querySelector('input[type="radio"]');
    if (!input) {
      input = h("input", { type: "radio" });
      forward(this, input);
      input.checked = this.flag("checked");
    }
    this._use(input);

    const card = input.closest(".i80-form-radio-card") ?? h("label", { "data-i80-part": "card" });
    this._card = card;
    const content = card.querySelector(":scope > .i80-form-radio-card__content");
    this._content = content ?? h("span", { class: "i80-form-radio-card__content" });
    // Whatever the author wrote (extra content, a badge of their own) stays, after our parts.
    // In a clone of a rendered card it is already in place: everything that is not ours.
    this._extra = content
      ? [...content.childNodes].filter((n) => !(n.nodeType === Node.ELEMENT_NODE && n.hasAttribute("data-i80-part")))
      : this.takeContent([input]);
    if (!card.isConnected) {
      const wrapper = input.closest(".i80-form-radio-card__control-wrapper") ?? h("span", { class: "i80-form-radio-card__control-wrapper" }, input);
      card.append(this._content, wrapper);
      this.append(card);
    }
  }

  /** Take the radio as ours: the group's name, the checked classes, and it is our part. */
  _use(input) {
    input.setAttribute("data-i80-part", "control");
    const groupName = this.closest("i80-radio-card-group")?.getAttribute("name");
    if (groupName && !input.name) input.name = groupName;
    input.addEventListener("change", () => {
      this._touched = true;
      syncChecked(input);
    });
    this._input = input;
  }

  adopt() {
    const late = this.takeContent([this._input]);
    // A server-rendered radio that arrives after the element replaces the one we created.
    const control = late.find((n) => n.nodeType === Node.ELEMENT_NODE && n.matches('input[type="radio"]'));
    if (control) {
      this._input.replaceWith(control);
      this._use(control);
    }
    this._extra.push(...late.filter((n) => n !== control));
  }

  update() {
    const input = this._input;
    // The group re-renders its cards; one the browser has not upgraded yet builds itself.
    if (!input) return;
    if (this.hasAttribute("checked") && !this._touched) input.checked = this.flag("checked");
    if (this.hasAttribute("disabled")) input.disabled = this.flag("disabled");
    const group = this.closest("i80-radio-card-group");
    // A card follows the group unless it says otherwise itself.
    const choice = (name, allowed, fallback) => {
      const value = this.getAttribute(name) ?? group?.getAttribute(name);
      return allowed.includes(value) ? value : fallback;
    };
    const position = choice("control-position", POSITIONS, "bottom");
    const alignment = choice("alignment", ALIGNMENTS, "left");
    const maxWidth = this.getAttribute("max-width");

    this._card.className = [
      "i80-form-radio-card",
      input.checked && "i80-form-radio-card--checked",
      input.disabled && "i80-form-radio-card--disabled",
      maxWidth ? "i80-form-radio-card--has-fixed-width" : "i80-form-radio-card--has-fluid-width",
      `i80-form-radio-card--control-${position}`,
      `i80-form-radio-card--align-${alignment}`,
    ].filter(Boolean).join(" ");
    this._card.style.maxWidth = maxWidth ?? "";
    input.className = "i80-form-radio i80-form-radio-card__control";

    const name = this.getAttribute("icon");
    const badge = this.getAttribute("badge");
    const label = this.getAttribute("label");
    const description = this.getAttribute("description");
    const iconEl = name ? icon(name, { size: "24" }) : null;
    iconEl?.setAttribute("data-i80-part", "icon");
    this._content.replaceChildren(
      ...(iconEl ? [iconEl] : []),
      ...(label ? [h("span", { class: "i80-text i80-typography-display-300 i80-font-weight-bold i80-form-radio-card__label", "data-i80-part": "label" }, label)] : []),
      ...(badge ? [h("div", { class: "i80-badge i80-badge--size-medium i80-badge--type-filled i80-badge--color-neutral", "data-i80-part": "badge" }, h("div", { class: "i80-badge__text" }, badge))] : []),
      ...(description ? [h("span", { class: "i80-text i80-typography-body-100 i80-font-weight-regular i80-form-radio-card__description", "data-i80-part": "description" }, description)] : []),
      ...this._extra,
    );
  }

  get checked() {
    return this._input?.checked ?? this.flag("checked");
  }

  set checked(v) {
    if (!this._input) return;
    this._input.checked = !!v;
    this._touched = true;
    syncChecked(this._input);
    this.update();
  }

  get value() {
    return this._input?.value ?? "";
  }
}

/**
 * <i80-radio-card-group legend="Cluster type" name="type" alignment="center">
 *   <i80-radio-card label="Managed" value="managed" checked></i80-radio-card>
 *   <i80-radio-card label="Self-managed" value="self"></i80-radio-card>
 * </i80-radio-card-group>
 *
 * The same fieldset as the other groups (legend, helper text, error, one name for every
 * control), laid out for cards: horizontal unless layout="vertical".
 */
export class I80RadioCardGroup extends I80RadioGroup {
  static get observedAttributes() {
    return [...super.observedAttributes, "alignment", "control-position"];
  }

  update() {
    super.update();
    const layout = this.choice("layout", ["vertical", "horizontal"], "horizontal");
    this._fs.className = `i80-form-group i80-form-group--radio-cards i80-form-group--layout-${layout}`;
    for (const card of this._fs.querySelectorAll("i80-radio-card")) card.update?.();
  }
}

define("i80-radio-card", I80RadioCard);
define("i80-radio-card-group", I80RadioCardGroup);
